import MarketplaceAccessSchema, { MarketplaceAccessParams, MarketplaceAccessJSON } from "permissions/resources/marketplace/_schema/marketplace-access-schema";
import MarketplaceDirectoryAccessSchema, { MarketplaceDirectoryAccessJSON, MarketplaceDirectoryAccessParams } from "permissions/resources/marketplace/_schema/marketplace-directory-access-schema";
import MarketplaceSearchAccessSchema, { MarketplaceSearchAccessJSON, MarketplaceSearchAccessParams } from "permissions/resources/marketplace/_schema/marketplace-search-access-schema";
import { Resource } from "permissions/resources/permission-resource";
import { registerResource } from "permissions/resources/resource-registry";
import { AccessLevel, AnyResourceJSON, ResourceFromJSON, ResourceType, Scope } from "permissions/resources/permission-resource-types";

export class MarketplaceAccess extends Resource {
  public static create(params?: MarketplaceAccessParams): MarketplaceAccess {
    const parsed = MarketplaceAccessSchema.validate(params);
    return new MarketplaceAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = MarketplaceAccessSchema.deserialize(json as MarketplaceAccessJSON);
    return new MarketplaceAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): MarketplaceAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(Number)],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(Number)],
    ];
    return this.generatePermutations(MarketplaceAccess, fieldIterations) as MarketplaceAccess[];
  }

  public readonly resourceType = ResourceType.MarketplaceAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: MarketplaceAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }
  
  public toJSON(): MarketplaceAccessJSON {
    return MarketplaceAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `MarketplaceAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel},
      scope: ${this.scope}
    }`;
  }
}

registerResource("MarketplaceAccess", (json) => MarketplaceAccess.fromJSON(json));



export class MarketplaceSearchAccess extends Resource {
  public static create(params?: MarketplaceSearchAccessParams): MarketplaceSearchAccess {
    const parsed = MarketplaceSearchAccessSchema.validate(params);
    return new MarketplaceSearchAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = MarketplaceSearchAccessSchema.deserialize(json as MarketplaceSearchAccessJSON);
    return new MarketplaceSearchAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): MarketplaceSearchAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(Number)],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(Number)],
    ];
    return this.generatePermutations(MarketplaceSearchAccess, fieldIterations) as MarketplaceSearchAccess[];
  }

  public readonly resourceType = ResourceType.MarketplaceSearchAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: MarketplaceSearchAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }
  
  public toJSON(): MarketplaceSearchAccessJSON {
    return MarketplaceSearchAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `MarketplaceSearchAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel},
      scope: ${this.scope}
    }`;
  }
}

registerResource("MarketplaceSearchAccess", (json) => MarketplaceSearchAccess.fromJSON(json));



/**
 * Permission to turn an identifier this session already holds into the person behind it: the
 * marketplace's by-id individual lookups.
 *
 * Deliberately not the same thing as {@link MarketplaceSearchAccess}. Search is discovery and hands
 * back people the caller had never heard of; resolution answers "who is this" about an id the
 * caller was already given by something they are entitled to, such as a proposal they are on. A
 * restricted onboarding session holds this and not search, which is how the onboarding proposal's
 * roster names its people without that session gaining the run of the marketplace.
 */
export class MarketplaceDirectoryAccess extends Resource {
  public static create(params?: MarketplaceDirectoryAccessParams): MarketplaceDirectoryAccess {
    const parsed = MarketplaceDirectoryAccessSchema.validate(params);
    return new MarketplaceDirectoryAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = MarketplaceDirectoryAccessSchema.deserialize(json as MarketplaceDirectoryAccessJSON);
    return new MarketplaceDirectoryAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): MarketplaceDirectoryAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(Number)],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(Number)],
    ];
    return this.generatePermutations(MarketplaceDirectoryAccess, fieldIterations) as MarketplaceDirectoryAccess[];
  }

  public readonly resourceType = ResourceType.MarketplaceDirectoryAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: MarketplaceDirectoryAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): MarketplaceDirectoryAccessJSON {
    return MarketplaceDirectoryAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `MarketplaceDirectoryAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel},
      scope: ${this.scope}
    }`;
  }
}

registerResource("MarketplaceDirectoryAccess", (json) => MarketplaceDirectoryAccess.fromJSON(json));
